import {
  PricesRangeWithTotal,
  PricingRangeWithDiscountAndTotalProps,
} from '../types';
import { getDiscountField } from './getDiscountField';

/**
 * Computes applicable discounts by comparing base prices with discount prices
 *
 * @param props - Input parameters
 * @param props.pricesRange - Base pricing structure
 * @param props.discountPrices - Optional discounted pricing structure
 *
 * @returns Discount object containing reduced prices for each field:
 * ```ts
 * {
 *   price: number | null,    // Per-employee price discount
 *   base: number | null,     // Base price discount
 *   total: number | null,    // Total price discount
 *   obFees: number | null    // Onboarding fees discount
 * }
 * ```
 * Returns undefined if either pricesRange or discountPrices is missing
 *
 * @example
 * ```ts
 * const result = getDiscount({
 *   pricesRange: {
 *     base: 100,
 *     price: 50,
 *     total: 300
 *   },
 *   discountPrices: {
 *     base: 80,
 *     price: 40,
 *     total: 240
 *   }
 * });
 * // Returns: {
 * //   base: 80,    // Discounted from 100
 * //   price: 40,   // Discounted from 50
 * //   total: 240,  // Discounted from 300
 * //   obFees: null // No discount available
 * // }
 * ```
 */

export type GetDiscountProps = {
  pricesRange?: PricingRangeWithDiscountAndTotalProps | null;
  discountPrices?: PricesRangeWithTotal | null;
};

export const getDiscount = ({
  pricesRange,
  discountPrices,
}: GetDiscountProps): PricesRangeWithTotal | undefined => {
  if (!pricesRange || !discountPrices) return;
  const price = getDiscountField('price', pricesRange, discountPrices);
  const base = getDiscountField('base', pricesRange, discountPrices);
  const obFees = getDiscountField('obFees', pricesRange, discountPrices);
  const total = getDiscountField(
    'total',
    pricesRange,
    discountPrices,
  );

  return {
    price,
    base,
    total,
    obFees,
  };
};
